/* istanbul ignore file - better tested visually */

import {
  faBackwardStep,
  faDiamond as faHollowDiamond,
  faForwardStep,
  faPause,
  faPlay,
  faRepeat,
  faStop,
} from '@fortawesome/pro-light-svg-icons';
import { faDiamond } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimationState } from '@partful/features/instructions/animations/model/animationState';
import { classNames } from '@partful/shared/lib/classNames';
import { Button } from '@partful/shared/ui/Button';
import { Surface, SurfaceClipShadow } from '@partful/shared/ui/Surface';
import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './AnimationToolbar.module.scss';

export type AnimationToolbarProps = {
  disabled?: boolean;
  animationState: AnimationState;
  canSaveKeyframe: boolean;
  onSaveKeyframe: () => void;
  canRemoveKeyframe: boolean;
  onRemoveKeyframe: () => void;
  onGoToStart: () => void;
  onGoToEnd: () => void;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  isLooping: boolean;
  onLoop: () => void;
} & ComponentProps<'div'>;

export function AnimationToolbar({
  disabled,
  animationState,
  canSaveKeyframe,
  onSaveKeyframe,
  canRemoveKeyframe,
  onRemoveKeyframe,
  onGoToStart,
  onGoToEnd,
  onPlay,
  onPause,
  onStop,
  isLooping,
  onLoop,
  className,
  ...rest
}: Readonly<AnimationToolbarProps>) {
  const { t: i18n } = useTranslation();

  return (
    <SurfaceClipShadow className={classNames(styles.animationToolbar, className)} shadow="large" {...rest}>
      <div className={styles.toolbar}>
        <Surface className={styles.keyframeActions} clip="top" clipSize="small">
          <Button size="small" variant="secondary" onClick={onSaveKeyframe} disabled={disabled || !canSaveKeyframe}>
            <FontAwesomeIcon icon={faDiamond} />
            {i18n('instructions:stepBuilder.editor.saveKeyframe')}
          </Button>
          <Button size="small" variant="secondary" onClick={onRemoveKeyframe} disabled={disabled || !canRemoveKeyframe}>
            <FontAwesomeIcon icon={faHollowDiamond} />
            {i18n('instructions:stepBuilder.editor.removeKeyframe')}
          </Button>
        </Surface>
        <Surface className={styles.playerActions} clip="top" clipSize="small">
          <Button
            icon
            size="small"
            variant="primary"
            title={i18n('instructions:stepBuilder.editor.goToStart')}
            disabled={disabled || animationState === AnimationState.Playing}
            onClick={onGoToStart}
          >
            <FontAwesomeIcon icon={faBackwardStep} />
          </Button>
          <Button
            icon
            size="small"
            variant="primary"
            title={i18n(
              animationState === AnimationState.Playing
                ? 'instructions:stepBuilder.editor.pause'
                : 'instructions:stepBuilder.editor.play'
            )}
            disabled={disabled}
            onClick={animationState === AnimationState.Playing ? onPause : onPlay}
          >
            <FontAwesomeIcon icon={animationState === AnimationState.Playing ? faPause : faPlay} />
          </Button>
          <Button
            icon
            size="small"
            variant="primary"
            title={i18n('instructions:stepBuilder.editor.stop')}
            disabled={disabled || animationState === AnimationState.Stopped}
            onClick={onStop}
          >
            <FontAwesomeIcon icon={faStop} />
          </Button>
          <Button
            icon
            size="small"
            variant="primary"
            title={i18n('instructions:stepBuilder.editor.goToEnd')}
            disabled={disabled || animationState === AnimationState.Playing}
            onClick={onGoToEnd}
          >
            <FontAwesomeIcon icon={faForwardStep} />
          </Button>
          <Button
            icon
            size="small"
            variant={isLooping ? 'primary' : 'secondary'}
            title={i18n('instructions:stepBuilder.editor.repeat')}
            disabled={disabled}
            onClick={onLoop}
          >
            <FontAwesomeIcon icon={faRepeat} />
          </Button>
        </Surface>
      </div>
    </SurfaceClipShadow>
  );
}
